/**
 * FactCheckDemo - Real-time fact-checking visualization using backend SSE streaming
 *
 * Shows:
 * - 3 research agents searching in parallel (real API calls)
 * - Each agent's findings with clickable full output and sources
 * - Fact-checker analyzing and validating claims
 * - Discrepancies and key findings from fact-checker
 * - Final confidence score
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import {
  Shield,
  Search,
  Globe,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock,
} from 'lucide-react';
import { getProviderFromModel, getProviderLogo, getProviderColor as getProviderColorUtil } from '@/utils/providerUtils';

// Types for SSE events
interface WebSource {
  url: string;
  title: string;
  description: string;
}

interface AgentData {
  name: string;
  provider: string;
  model: string;
  status: 'pending' | 'researching' | 'complete' | 'error';
  progress: number;
  outputPreview: string;
  fullOutput: string;
  latencySeconds: number;
  webSources: WebSource[];
  error?: string;
}

interface FactCheckData {
  model: string;
  provider: string;
  status: 'pending' | 'checking' | 'complete';
  confidenceScore: number;
  isValidated: boolean;
  keyFindings: string[];
  discrepancies: string[];
  needsDebate: boolean;
}

interface FactCheckDemoProps {
  question: string;
  onClose?: () => void;
  className?: string;
  /** Real browser fingerprint for showcase limit tracking */
  fingerprintId?: string;
  /** Callback when showcase limit is reached */
  onLimitReached?: () => void;
}

type Phase = 'connecting' | 'researching' | 'fact-checking' | 'complete' | 'error';

export function FactCheckDemo({ question, onClose, className, fingerprintId, onLimitReached }: FactCheckDemoProps) {
  const [phase, setPhase] = useState<Phase>('connecting');
  const [agents, setAgents] = useState<AgentData[]>([]);
  const [factCheck, setFactCheck] = useState<FactCheckData | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // SSE streaming from backend
  const startResearch = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setPhase('connecting');
    setAgents([]);
    setFactCheck(null);
    setExpandedAgent(null);
    setErrorMessage(null);

    try {
      // Use real fingerprint for showcase limit tracking, fallback to timestamp-based ID
      // Note: For fact-check demos, we use force_debate=true to ensure showcase tracking
      const effectiveFingerprint = fingerprintId || `demo_${Date.now()}`;

      const response = await fetch(`${API_BASE_URL}/api/v1/free-report/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: question,
          turnstile_token: 'XXXX.DEMO.TOKEN.XXXX',
          tasks: [{ name: 'Research', description: question, execution_mode: 'parallel' }],
          fingerprint_id: effectiveFingerprint,
          force_debate: true, // Enable showcase tracking (counts as demo)
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.startsWith('event: ')) {
            const eventType = line.slice(7).trim();
            const dataLine = lines[i + 1];
            if (dataLine?.startsWith('data: ')) {
              const data = JSON.parse(dataLine.slice(6));
              handleSSEEvent(eventType, data);
              i++; // Skip the data line since we already processed it
            }
          } else if (line.startsWith('data: ') && !lines[i - 1]?.startsWith('event: ')) {
            // Handle data-only lines (parse event type from data)
            try {
              const data = JSON.parse(line.slice(6));
              if (data.event) handleSSEEvent(data.event, data);
            } catch { /* ignore parse errors */ }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('SSE Error:', err);
        setErrorMessage((err as Error).message || 'Connection failed');
        setPhase('error');
      }
    }
  }, [question]);

  // Handle SSE events
  const handleSSEEvent = useCallback((eventType: string, data: Record<string, unknown>) => {
    console.log('[FactCheckDemo] SSE Event:', eventType, data);
    switch (eventType) {
      case 'agent_start':
        console.log('[FactCheckDemo] agent_start - agent_index:', data.agent_index, 'agent_name:', data.agent_name);
        setPhase('researching');
        setAgents(prev => {
          const newAgents = [...prev];
          const idx = data.agent_index as number;
          while (newAgents.length <= idx) {
            newAgents.push({
              name: '', provider: '', model: '', status: 'pending',
              progress: 0, outputPreview: '', fullOutput: '', latencySeconds: 0, webSources: [],
            });
          }
          newAgents[idx] = {
            ...newAgents[idx],
            name: data.agent_name as string || data.model as string,
            provider: (data.provider as string)?.toLowerCase() || 'openai',
            model: data.model as string,
            status: 'researching',
            progress: 10,
          };
          return newAgents;
        });
        break;

      case 'agent_complete':
        setAgents(prev => {
          const newAgents = [...prev];
          const idx = data.agent_index as number;
          if (newAgents[idx]) {
            newAgents[idx] = {
              ...newAgents[idx],
              status: data.success ? 'complete' : 'error',
              progress: 100,
              outputPreview: data.output_preview as string || '',
              fullOutput: data.full_output as string || data.output_preview as string || '',
              latencySeconds: data.latency_seconds as number || 0,
              webSources: (data.web_sources as WebSource[]) || [],
              error: data.error as string,
            };
          }
          return newAgents;
        });
        break;

      case 'fact_check_start':
        setPhase('fact-checking');
        setFactCheck({
          model: data.model as string,
          provider: (data.provider as string)?.toLowerCase() || 'google',
          status: 'checking',
          confidenceScore: 0,
          isValidated: false,
          keyFindings: [],
          discrepancies: [],
          needsDebate: false,
        });
        break;

      case 'fact_check_complete':
        setFactCheck(prev => ({
          ...prev!,
          status: 'complete',
          confidenceScore: data.confidence_score as number || 0,
          isValidated: data.is_validated as boolean || false,
          keyFindings: (data.key_findings as string[]) || [],
          discrepancies: (data.discrepancies as string[]) || [],
          needsDebate: data.needs_debate as boolean || false,
        }));
        setPhase('complete');
        break;

      case 'error':
        setErrorMessage(data.error as string || 'An error occurred');
        setPhase('error');
        // Check if showcase demo limit was reached
        if (data.limit_reached && onLimitReached) {
          onLimitReached();
        }
        break;

      case 'done':
        if (phase !== 'complete') setPhase('complete');
        break;
    }
  }, [phase, onLimitReached]);

  // Start research on mount
  useEffect(() => {
    startResearch();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [startResearch]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [phase, agents, factCheck, expandedAgent]);

  return (
    <div className={cn(
      'relative flex flex-col h-full rounded-xl border-2 overflow-hidden',
      'backdrop-blur-md bg-gradient-to-br from-emerald-500/10 to-teal-500/5',
      'border-emerald-500/30 shadow-xl',
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border-subtle">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Fact-Checking in Action</h3>
            <p className="text-xs text-text-secondary line-clamp-1 max-w-[300px]">"{question}"</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-light transition-colors">
            <X className="w-4 h-4 text-text-secondary" />
          </button>
        )}
      </div>

      {/* Content - scrollable */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Connecting state */}
        {phase === 'connecting' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mr-3" />
            <span className="text-sm text-text-secondary">Connecting to research agents...</span>
          </div>
        )}

        {/* Error state */}
        {phase === 'error' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <span className="text-sm text-red-400">{errorMessage || 'Connection failed'}</span>
            <button
              onClick={startResearch}
              className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/30 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Research Agents Section */}
        {(phase === 'researching' || phase === 'fact-checking' || phase === 'complete') && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-text-secondary font-mono">
              <Search className="w-4 h-4" />
              <span>Research Agents ({agents.length})</span>
              {phase === 'researching' && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
            </div>

            {agents.map((agent, idx) => {
              const normalizedProvider = getProviderFromModel(agent.provider || agent.model);
              const isComplete = agent.status === 'complete';
              const isExpanded = expandedAgent === idx;

              return (
                <div
                  key={idx}
                  className={cn(
                    'rounded-xl border transition-all duration-300 overflow-hidden',
                    getProviderColorUtil(normalizedProvider, 'border'),
                    isComplete ? 'bg-surface-light' : 'bg-white/[0.02]',
                    isComplete && 'cursor-pointer hover:bg-surface-light'
                  )}
                  onClick={() => isComplete && setExpandedAgent(isExpanded ? null : idx)}
                >
                  <div className="p-3">
                    {/* Agent header */}
                    <div className="flex items-center gap-2">
                      <div className={cn('w-6 h-6 rounded-full flex items-center justify-center', getProviderColorUtil(normalizedProvider, 'bg'))}>
                        <span className="flex items-center justify-center">{getProviderLogo(normalizedProvider, 14)}</span>
                      </div>
                      <span className={cn('text-xs font-mono font-medium', getProviderColorUtil(normalizedProvider, 'text'))}>{agent.name || 'Agent'}</span>
                      {isComplete && (
                        <div className="flex items-center gap-2 ml-auto">
                          <span className="text-[10px] text-text-muted flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {agent.latencySeconds.toFixed(1)}s
                          </span>
                          <span className="text-[10px] text-text-muted">
                            {agent.webSources.length} sources
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-text-secondary" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-text-secondary" />
                          )}
                        </div>
                      )}
                      {agent.status === 'researching' && (
                        <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin ml-auto" />
                      )}
                      {agent.status === 'error' && (
                        <AlertTriangle className="w-3.5 h-3.5 text-red-400 ml-auto" />
                      )}
                    </div>

                    {/* Progress bar for researching */}
                    {agent.status === 'researching' && (
                      <div className="h-1 bg-surface-light rounded-full overflow-hidden mt-2">
                        <div
                          className={cn('h-full transition-all duration-200 animate-pulse', getProviderColorUtil(normalizedProvider, 'bg'))}
                          style={{ width: '60%' }}
                        />
                      </div>
                    )}

                    {/* Preview when complete but not expanded */}
                    {isComplete && !isExpanded && (
                      <p className="text-xs text-text-secondary mt-2 line-clamp-2">{agent.outputPreview}</p>
                    )}
                  </div>

                  {/* Expanded content */}
                  {isComplete && isExpanded && (
                    <div className="border-t border-border-subtle p-3 space-y-3 animate-fadeIn">
                      {/* Web Sources */}
                      {agent.webSources.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1.5 text-xs text-text-muted">
                            <Globe className="w-3 h-3" />
                            <span className="font-mono">Sources Used:</span>
                          </div>
                          <div className="space-y-1.5">
                            {agent.webSources.map((source, sIdx) => (
                              <a
                                key={sIdx}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-start gap-2 p-2 rounded-lg bg-surface-light hover:bg-surface-light transition-colors group"
                              >
                                <ExternalLink className="w-3 h-3 text-text-muted mt-0.5 flex-shrink-0 group-hover:text-emerald-400" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-text-primary font-medium truncate group-hover:text-emerald-400">
                                    {source.title || source.url}
                                  </p>
                                  {source.description && (
                                    <p className="text-[10px] text-text-muted line-clamp-2">{source.description}</p>
                                  )}
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Full Output */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs text-text-muted">
                          <CheckCircle2 className="w-3 h-3" />
                          <span className="font-mono">Full Research Output:</span>
                        </div>
                        <div className="p-3 rounded-lg bg-surface/50 max-h-64 overflow-y-auto prose prose-invert prose-xs max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-headings:mt-2 prose-headings:mb-1 prose-strong:text-text-primary">
                          <ReactMarkdown>
                            {agent.fullOutput || agent.outputPreview}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Fact Checker Section */}
        {(phase === 'fact-checking' || phase === 'complete') && factCheck && (
          <div className="animate-fadeIn space-y-3 pt-2 border-t border-border-subtle">
            <div className="flex items-center gap-2 text-xs text-yellow-400 font-mono">
              <Shield className="w-4 h-4" />
              <span>Fact Verification</span>
              <span className="text-text-muted">({factCheck.model})</span>
            </div>

            <div className={cn(
              'p-4 rounded-xl border-2 transition-all duration-500',
              factCheck.status === 'complete'
                ? factCheck.discrepancies.length > 0
                  ? 'bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/40'
                  : 'bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/40'
                : 'bg-gradient-to-br from-yellow-500/10 to-amber-600/5 border-yellow-500/30'
            )}>
              {factCheck.status === 'checking' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                    <span className="text-sm text-text-primary">Validating claims against sources...</span>
                  </div>
                  <div className="h-2 bg-surface-light rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-500 animate-pulse w-2/3" />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Confidence score */}
                  <div className="flex items-center justify-between">
                    <span className={cn(
                      'text-sm font-medium',
                      factCheck.discrepancies.length > 0 ? 'text-amber-400' : 'text-emerald-400'
                    )}>
                      Verification Complete
                    </span>
                    <span className={cn(
                      'px-2 py-1 rounded-full text-sm font-bold',
                      factCheck.confidenceScore >= 0.8
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : factCheck.confidenceScore >= 0.6
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-red-500/20 text-red-400'
                    )}>
                      {(factCheck.confidenceScore * 100).toFixed(0)}% Confidence
                    </span>
                  </div>

                  {/* Key findings */}
                  {factCheck.keyFindings.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5 text-xs text-emerald-500">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span className="font-mono">Key Findings:</span>
                      </div>
                      <ul className="space-y-1.5">
                        {factCheck.keyFindings.map((finding, idx) => (
                          <li
                            key={idx}
                            className="text-xs text-text-primary pl-3 border-l-2 border-emerald-500/40 bg-emerald-500/5 rounded-r py-1.5 pr-2"
                          >
                            {finding}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Discrepancies */}
                  {factCheck.discrepancies.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5 text-xs text-amber-500">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span className="font-mono">Discrepancies Found:</span>
                      </div>
                      <ul className="space-y-1.5">
                        {factCheck.discrepancies.map((disc, idx) => (
                          <li
                            key={idx}
                            className="text-xs text-text-primary pl-3 border-l-2 border-amber-500/40 bg-amber-500/5 rounded-r py-1.5 pr-2"
                          >
                            {disc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Needs debate indicator */}
                  {factCheck.needsDebate && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <span className="text-xs text-amber-400">Low confidence - Debate recommended</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className="p-3 border-t border-border-subtle bg-bar-bg/30">
        <div className="flex items-center gap-2">
          {phase === 'connecting' && (
            <>
              <Loader2 className="w-4 h-4 text-text-secondary animate-spin" />
              <span className="text-xs text-text-secondary">Connecting...</span>
            </>
          )}
          {phase === 'researching' && (
            <>
              <Search className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span className="text-xs text-text-secondary">Agents researching across multiple sources...</span>
            </>
          )}
          {phase === 'fact-checking' && (
            <>
              <Shield className="w-4 h-4 text-yellow-400 animate-pulse" />
              <span className="text-xs text-text-secondary">Fact-checker validating claims...</span>
            </>
          )}
          {phase === 'complete' && factCheck && (
            <>
              <CheckCircle2 className={cn(
                'w-4 h-4',
                factCheck.discrepancies.length > 0 ? 'text-amber-400' : 'text-emerald-400'
              )} />
              <span className={cn(
                'text-xs',
                factCheck.discrepancies.length > 0 ? 'text-amber-400' : 'text-emerald-400'
              )}>
                Verification complete - {factCheck.discrepancies.length > 0
                  ? `${factCheck.discrepancies.length} discrepancies found`
                  : 'High confidence'}
              </span>
            </>
          )}
          {phase === 'error' && (
            <>
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-error">Error occurred</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

